// src/utils/listNormalizers.ts
// Normalizers for list API responses to handle backward compatibility
// with legacy array responses vs new paginated wrapper responses

import type {
  Contact,
  ContactsResponse,
  Task,
  TasksResponse,
  CommunicationThread,
  ThreadsResponse,
  CommunicationMessage,
  MessagesResponse,
  Pagination,
} from '../types/api';

/**
 * Normalize contacts list response.
 * Handles both new wrapper format and legacy array format.
 */
export function normalizeContactsListResponse(
  data: Contact[] | ContactsResponse | unknown
): ContactsResponse {
  // If it's already a wrapper with contacts array
  if (data && typeof data === 'object' && 'contacts' in data && Array.isArray((data as ContactsResponse).contacts)) {
    const wrapped = data as ContactsResponse;
    return {
      contacts: wrapped.contacts,
      pagination: wrapped.pagination || {
        total: wrapped.contacts.length,
        limit: wrapped.contacts.length,
        offset: 0,
      },
      isFallbackArrayMode: false,
    };
  }

  // If it's a legacy array response
  if (Array.isArray(data)) {
    return {
      contacts: data as Contact[],
      pagination: {
        total: data.length,
        limit: data.length,
        offset: 0,
      },
      isFallbackArrayMode: true,
    };
  }

  // Fallback: empty response
  return {
    contacts: [],
    pagination: { total: 0, limit: 0, offset: 0 },
    isFallbackArrayMode: true,
  };
}

/**
 * Normalize tasks list response.
 * Handles both new wrapper format and legacy array format.
 */
export function normalizeTasksListResponse(
  data: Task[] | TasksResponse | unknown
): TasksResponse {
  // If it's already a wrapper with tasks array
  if (data && typeof data === 'object' && 'tasks' in data && Array.isArray((data as TasksResponse).tasks)) {
    const wrapped = data as TasksResponse;
    return {
      tasks: wrapped.tasks,
      pagination: wrapped.pagination || {
        total: wrapped.tasks.length,
        limit: wrapped.tasks.length,
        offset: 0,
      },
      isFallbackArrayMode: false,
    };
  }

  // If it's a legacy array response
  if (Array.isArray(data)) {
    return {
      tasks: data as Task[],
      pagination: {
        total: data.length,
        limit: data.length,
        offset: 0,
      },
      isFallbackArrayMode: true,
    };
  }

  // Fallback: empty response
  return {
    tasks: [],
    pagination: { total: 0, limit: 0, offset: 0 },
    isFallbackArrayMode: true,
  };
}

/**
 * Normalize threads list response.
 * Handles both new wrapper format and legacy array format.
 */
export function normalizeThreadsListResponse(
  data: CommunicationThread[] | ThreadsResponse | unknown
): ThreadsResponse {
  // If it's already a wrapper with threads array
  if (data && typeof data === 'object' && 'threads' in data && Array.isArray((data as ThreadsResponse).threads)) {
    const wrapped = data as ThreadsResponse;
    return {
      threads: wrapped.threads,
      pagination: wrapped.pagination || {
        total: wrapped.threads.length,
        limit: wrapped.threads.length,
        offset: 0,
      },
      isFallbackArrayMode: false,
    };
  }

  // If it's a legacy array response
  if (Array.isArray(data)) {
    return {
      threads: data as CommunicationThread[],
      pagination: {
        total: data.length,
        limit: data.length,
        offset: 0,
      },
      isFallbackArrayMode: true,
    };
  }

  // Fallback: empty response
  return {
    threads: [],
    pagination: { total: 0, limit: 0, offset: 0 },
    isFallbackArrayMode: true,
  };
}

/**
 * Normalize messages list response.
 * Handles both new wrapper format and legacy array format.
 */
export function normalizeMessagesListResponse(
  data: CommunicationMessage[] | MessagesResponse | unknown
): MessagesResponse {
  // If it's already a wrapper with messages array
  if (data && typeof data === 'object' && 'messages' in data && Array.isArray((data as MessagesResponse).messages)) {
    const wrapped = data as MessagesResponse;
    return {
      messages: wrapped.messages,
      pagination: wrapped.pagination || {
        total: wrapped.messages.length,
        limit: wrapped.messages.length,
        offset: 0,
      },
      isFallbackArrayMode: false,
    };
  }

  // If it's a legacy array response
  if (Array.isArray(data)) {
    return {
      messages: data as CommunicationMessage[],
      pagination: {
        total: data.length,
        limit: data.length,
        offset: 0,
      },
      isFallbackArrayMode: true,
    };
  }

  // Fallback: empty response
  return {
    messages: [],
    pagination: { total: 0, limit: 0, offset: 0 },
    isFallbackArrayMode: true,
  };
}

/**
 * Check if pagination has more pages.
 * Returns false in fallback mode (no real pagination).
 */
export function hasMorePages(pagination: Pagination, isFallbackArrayMode?: boolean): boolean {
  if (isFallbackArrayMode) {
    return false;
  }
  return pagination.offset + pagination.limit < pagination.total;
}

/**
 * Check if we can go to previous page.
 * Returns false in fallback mode.
 */
export function hasPreviousPage(pagination: Pagination, isFallbackArrayMode?: boolean): boolean {
  if (isFallbackArrayMode) {
    return false;
  }
  return pagination.offset > 0;
}
